import * as React from 'react';

import { BoxProps } from '@leagueplatform/genesis-commons';
import { Box, queryHelpers, useMediaQuery } from '@leagueplatform/genesis-core';
import { getModulePath, LEAGUE_MODULE_NAMES } from '@leagueplatform/core';
import { useLocation } from '@leagueplatform/routing';
import { useScrollTopOnLocationChange } from '@leagueplatform/web-common';
import {
  APP_NAV_DEFAULT_HEIGHT,
  APP_NAV_MOBILE_HEIGHT,
  AuthedWebviewModal,
  DraggableLiveChat,
  ERROR_TYPES,
  ErrorState,
  INTERNAL_BASE_ROUTES,
  LinkHandler,
  LoadingSpinner,
  QueryParamModal,
  useSsoDeepLink,
  FEATURE_FLAGS,
  MODAL_PARAMS,
  APP_MANAGED_ROUTES,
  ImpersonationMasonryBanner,
} from '@highmark-web/highmark-common';
import { ObservabilityErrorBoundary } from '@leagueplatform/observability';
import { useGetAppFeatureFlag } from 'hooks/use-get-app-feature-flag.hook';
import { AppNavBarComponent } from './nav.component';
import { IelFooter } from './footer/iel-footer.component';

const ExcludeChatBubbleRoutes = [
  getModulePath(LEAGUE_MODULE_NAMES.healthJourney),
  getModulePath(LEAGUE_MODULE_NAMES.healthPrograms),
  getModulePath(LEAGUE_MODULE_NAMES.healthProgress),
  getModulePath(LEAGUE_MODULE_NAMES.healthJourneyHistory),
  INTERNAL_BASE_ROUTES.COORDINATION_OF_BENEFITS_BASE,
  APP_MANAGED_ROUTES.EASY_PLAN_FAQ,
];

export const PrivateBasePage: React.FC<BoxProps> = ({
  enableLiveChat,
  enableEbillRegister,
  children,
}) => {
  useScrollTopOnLocationChange();
  useSsoDeepLink(LinkHandler);

  const { isEnabled: isLiveChatNavigateAwayEnabled } = useGetAppFeatureFlag(
    FEATURE_FLAGS.LIVE_CHAT_NAVIGATE_AWAY,
  );

  const location = useLocation();
  // Get first path string before non character value (/, ?, etc)
  const basePath = location?.pathname?.match(/^\/[\w-]+/) ?? ['/'];
  const isChatExcludedPath = ExcludeChatBubbleRoutes.includes(basePath[0]);

  const isChatBubbleEnabled = Boolean(
    !isChatExcludedPath && enableLiveChat && isLiveChatNavigateAwayEnabled,
  );

  const { isEnabled: isPWPEnabled } = useGetAppFeatureFlag(FEATURE_FLAGS.PWP);
  const isMobile = useMediaQuery(queryHelpers.down('mobileLandscape'));

  const MAIN_CONTENT_MIN_HEIGHT = `calc(100vh - ${
    isMobile ? APP_NAV_MOBILE_HEIGHT : APP_NAV_DEFAULT_HEIGHT
  }px)`;

  const customModalParams = [];

  if (isPWPEnabled) {
    customModalParams.push(MODAL_PARAMS.PWP_DISCLAIMER);
  }

  if (enableEbillRegister) {
    customModalParams.push(MODAL_PARAMS.EBILL_REGISTRATION);
  }

  return (
    <>
      <AppNavBarComponent />
      <AuthedWebviewModal />
      <QueryParamModal additionalCustomModalParams={customModalParams} />
      <ImpersonationMasonryBanner />
      <Box
        as="main"
        id="main-content"
        css={{
          position: 'relative',
          flex: '1',
          overflowX: 'hidden',
          // This is so any page will take up the whole screen regardless of content
          minHeight: MAIN_CONTENT_MIN_HEIGHT,
        }}
      >
        <ObservabilityErrorBoundary
          fallback={
            <ErrorState
              errorType={ERROR_TYPES.API_ERROR}
              styleProps={{
                minHeight: MAIN_CONTENT_MIN_HEIGHT,
              }}
            />
          }
          errorContext={{
            context: {
              Context: { location: 'BasePage ErrorBoundary' },
            },
          }}
        >
          <React.Suspense fallback={<LoadingSpinner />}>
            {children}
          </React.Suspense>
        </ObservabilityErrorBoundary>
      </Box>
      <IelFooter />
      {isChatBubbleEnabled && <DraggableLiveChat />}
    </>
  );
};
